import React, { useState } from 'react';
import { Box, Typography, TextField, MenuItem, Button, CircularProgress, Stack } from '@mui/material';
import { SendOutlined } from '@mui/icons-material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { makeApiCall, getAuthHeaders } from '../utils/apiConfig';
import {
  ConfirmDialog,
  EmptyState,
  InlineMessage,
  ListSkeleton,
  PageContainer,
  PageHeader,
  SectionCard,
  StatusChip,
} from '../components/ui';

const BROADCAST_TYPES = [
  { value: 'system_update', label: 'System Update' },
  { value: 'new_camera', label: 'New Product Announcement' },
  { value: 'map_pricing_update', label: 'MAP Pricing Update' },
];

interface SentBroadcast {
  id: string;
  type: string | null;
  title: string;
  message: string;
  recipientCount: number | null;
  sentBy: string;
  sentAt?: any;
}

const typeLabelFor = (value: string | null | undefined) =>
  BROADCAST_TYPES.find(option => option.value === value)?.label || value || 'Announcement';

const formatSentAt = (value: any): string => {
  if (!value) return '';
  const date =
    value?.toDate?.() ||
    (value?._seconds ? new Date(value._seconds * 1000) : null) ||
    (value?.seconds ? new Date(value.seconds * 1000) : null) ||
    new Date(value);
  return Number.isNaN(date.getTime())
    ? ''
    : date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

export const AdminNotificationsPage: React.FC = () => {
  const { isAdmin } = useAuth();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [type, setType] = useState('system_update');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const {
    data: sent,
    isLoading: sentLoading,
    isError: sentError,
    refetch: refetchSent,
  } = useQuery({
    queryKey: ['admin-sent-broadcasts'],
    enabled: isAdmin,
    queryFn: async (): Promise<SentBroadcast[]> => {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/admin/notifications/sent', { headers });
      if (!res.ok) throw new Error('Failed to load sent announcements');
      const body = await res.json();
      return body.data || [];
    },
    refetchOnWindowFocus: false,
  });

  if (!isAdmin) {
    return (
      <PageContainer>
        <InlineMessage severity="error">Access denied. Admin privileges required.</InlineMessage>
      </PageContainer>
    );
  }

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      showToast('Title and message are required.', 'error');
      return;
    }
    setSending(true);
    try {
      const headers = await getAuthHeaders();
      const res = await makeApiCall('/admin/notifications', {
        method: 'POST',
        headers,
        body: JSON.stringify({ type, title: title.trim(), message: message.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.error || data.message || 'Failed to send announcement');
      }
      showToast(data.message || 'Announcement sent.', 'success');
      setTitle('');
      setMessage('');
      // The audit entry is written after the response; give it a moment before reading it back.
      setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['admin-sent-broadcasts'] });
      }, 1500);
    } catch (err: any) {
      showToast(err.message || 'Failed to send announcement', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Announcements"
        description="Send in-app announcements to every user on the platform. Users who have opted out of the matching notification type in their preferences will not receive it."
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: '2 1 560px', minWidth: 0 }}>
          <SectionCard title="New announcement" subtitle="Delivered to every user at once and cannot be recalled." padded>
            <Stack spacing={2.5}>
              <TextField
                select
                label="Notification type"
                value={type}
                onChange={e => setType(e.target.value)}
                fullWidth
                size="small"
              >
                {BROADCAST_TYPES.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                label="Title"
                value={title}
                onChange={e => setTitle(e.target.value)}
                fullWidth
                size="small"
                inputProps={{ maxLength: 120 }}
              />

              <TextField
                label="Message"
                value={message}
                onChange={e => setMessage(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 1000 }}
                helperText={`${message.length}/1000 characters`}
              />

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="contained"
                  startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendOutlined />}
                  onClick={() => setConfirmOpen(true)}
                  disabled={sending || !title.trim() || !message.trim()}
                  sx={{ textTransform: 'none' }}
                >
                  {sending ? 'Sending…' : 'Send to all users'}
                </Button>
              </Box>
            </Stack>
          </SectionCard>
        </Box>

        <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
          <SectionCard
            title="Sent"
            subtitle={sent && sent.length > 0 ? `${sent.length} most recent` : 'Announcements already sent'}
            padded
            actions={
              <Button size="small" onClick={() => refetchSent()} sx={{ textTransform: 'none' }}>
                Refresh
              </Button>
            }
          >
            {sentLoading ? (
              <ListSkeleton rows={4} rowHeight={64} />
            ) : sentError ? (
              <InlineMessage severity="error">
                Could not load sent announcements.{' '}
                <Button size="small" onClick={() => refetchSent()} sx={{ textTransform: 'none' }}>
                  Retry
                </Button>
              </InlineMessage>
            ) : !sent || sent.length === 0 ? (
              <EmptyState
                size="compact"
                title="Nothing sent yet"
                description="Announcements you send appear here with who sent them and how many users received them."
              />
            ) : (
              <Box sx={{ maxHeight: 640, overflowY: 'auto' }}>
                {sent.map(item => (
                  <Box
                    key={item.id}
                    sx={{ py: 1.5, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
                      <StatusChip label={typeLabelFor(item.type)} tone="neutral" />
                      <Typography variant="caption" color="text.secondary">
                        {formatSentAt(item.sentAt)}
                      </Typography>
                    </Stack>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
                      {item.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                    >
                      {item.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {item.sentBy}
                      {item.recipientCount != null ? ` · ${item.recipientCount} users` : ''}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </SectionCard>
        </Box>
      </Box>

      {/* The broadcast reaches every user and cannot be recalled, so preview it first. */}
      <ConfirmDialog
        open={confirmOpen}
        title="Send to all users?"
        message="This in-app notification will be delivered to all users (except those who opted out of this type) and cannot be recalled."
        preview={
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {typeLabelFor(type)}
            </Typography>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, wordBreak: 'break-word' }}>
              {title.trim()}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {message.trim()}
            </Typography>
          </Box>
        }
        confirmLabel="Send to all users"
        confirmColor="warning"
        loading={sending}
        onConfirm={() => {
          setConfirmOpen(false);
          void handleSend();
        }}
        onClose={() => setConfirmOpen(false)}
      />
    </PageContainer>
  );
};
